import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

/* =====================================================================
   LA CÁMARA QUE SE QUEDA ABIERTA · DIRECTRIZ-ALTA-POR-ESCANEO F7
   =====================================================================
   Igual que `mobile/EscanerCamara.jsx` (mismos formatos, misma regla de
   apagar la cámara al cerrar), con una diferencia: NO se para en la
   primera lectura. Cada lectura llega a `onCodigo`, y la bandeja decide
   si es nueva, si suma o si es la misma de hace un momento.

   `onCodigo` se lee por referencia: la pantalla lo cambia en cada render
   y reiniciar la cámara por eso la apagaría y prendería sin parar.
   ===================================================================== */
const AVISO_INSEGURO =
  'La cámara necesita una conexión segura. Si ves un aviso de certificado, '
  + 'instala el certificado de TEGO en este dispositivo. Mientras tanto, '
  + 'teclea el código o usa un lector USB.';

export default function CamaraContinua({ onCodigo, onCerrar }) {
  const ref = useRef(null);
  const aviso = useRef(onCodigo);
  const [errorCamara, setErrorCamara] = useState(null);
  const seguro = window.isSecureContext;
  const error = seguro ? errorCamara : AVISO_INSEGURO;

  useEffect(() => { aviso.current = onCodigo; }, [onCodigo]);

  useEffect(() => {
    if (!seguro) return undefined;
    let cerrada = false;
    const scanner = new Html5Qrcode('lector-continuo', {
      formatsToSupport: [
        Html5QrcodeSupportedFormats.QR_CODE,
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.UPC_A,
        Html5QrcodeSupportedFormats.UPC_E,
        Html5QrcodeSupportedFormats.ITF,
        Html5QrcodeSupportedFormats.CODE_128,
        // CATÁLOGO VIVO F5.3 (R-CV3): la bolsa de Digi-Key, Mouser o Steren trae
        // un Data Matrix con el número de parte y la cantidad.
        Html5QrcodeSupportedFormats.DATA_MATRIX,
      ],
    });
    const caja = ref.current;
    const arranque = scanner
      .start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 160 } },
        (texto) => { if (!cerrada) aviso.current?.(texto); },
        () => {},
      )
      .catch(() => setErrorCamara('No se pudo abrir la cámara. Revisa los permisos o usa un lector.'));
    return () => {
      cerrada = true;
      const pistas = () => [...(caja?.querySelectorAll('video') || [])]
        .flatMap((v) => v.srcObject?.getTracks?.() || []);
      const antes = pistas();
      Promise.resolve(arranque)
        .then(() => (scanner.isScanning ? scanner.stop() : undefined))
        .catch(() => {})
        .finally(() => { [...antes, ...pistas()].forEach((t) => t.stop()); });
    };
  }, [seguro]);

  return (
    <div className="space-y-2">
      <div id="lector-continuo" ref={ref} className="rounded-xl overflow-hidden bg-black" />
      {error && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2.5">{error}</div>
      )}
      <button type="button" onClick={onCerrar}
        className="w-full min-h-11 text-sm font-semibold text-gray-500 hover:text-gray-800 py-2">
        Cerrar cámara
      </button>
    </div>
  );
}
